import { ArrowRight, CheckCircle2, CircleDashed } from "lucide-react";

import { ErrorState, Loading } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { organisationActivationWorkspace } from "@/lib/api";
import { useData } from "@/lib/app-view";
import type { Organisation } from "@/lib/types";

type Step = {
  title: string;
  description: string;
  complete: boolean;
  evidence: string;
  action: string;
  view: string;
  optional?: boolean;
};

export function SetupGuide({
  organisation,
  refresh,
  navigate,
}: {
  organisation: Organisation;
  refresh: number;
  navigate: (view: string) => void;
}) {
  const { data, loading, error } = useData(
    () => organisationActivationWorkspace(organisation.id),
    [organisation.id, refresh],
  );
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const counts = data.counts;
  const steps: Step[] = [
    {
      title: "Organisation identity",
      description: "Confirm the legal identity, public workspace name and brand.",
      complete: true,
      evidence: `${organisation.name} is active`,
      action: "Review identity",
      view: "branding",
    },
    {
      title: "Locations and operating structure",
      description: "Add branches, departments or production units that own budgets and requests.",
      complete: counts.locations > 0,
      evidence: `${counts.locations} location${counts.locations === 1 ? "" : "s"} · ${counts.units} operating units`,
      action: "Configure structure",
      view: "budgets",
    },
    {
      title: "Approval controls",
      description: "Review value thresholds and the number of independent approvals required.",
      complete: counts.rules >= 2,
      evidence: `${counts.rules} active approval rules`,
      action: "Review approval rules",
      view: "budgets",
    },
    {
      title: "Team and approvers",
      description: "Invite the people who request, approve, buy, receive and reconcile.",
      complete: counts.activeMembers >= 2 && counts.activeApprovers >= 2,
      evidence: `${counts.activeMembers} active members · ${counts.activeApprovers} approvers/admins`,
      action: "Add team",
      view: "team",
    },
    {
      title: "Suppliers and catalogue",
      description: "Add or import the suppliers and frequently purchased items needed for the first RFQ.",
      complete: counts.vendors > 0 && counts.catalogueItems > 0,
      evidence: `${counts.vendors} suppliers · ${counts.catalogueItems} catalogue items`,
      action: counts.vendors ? "Add catalogue" : "Add suppliers",
      view: counts.vendors ? "inventory" : "vendors",
    },
    {
      title: "Budget control",
      description: "Connect requests to a branch, department or country operating budget.",
      complete: counts.budgets > 0,
      evidence: `${counts.budgets} active budgets`,
      action: "Configure budgets",
      view: "budgets",
      optional: true,
    },
    {
      title: "First governed purchase",
      description: "Create the request that begins your real Golden Journey.",
      complete: counts.requisitions > 0,
      evidence: `${counts.requisitions} requisitions recorded`,
      action: "Create first requisition",
      view: "requisitions",
    },
  ];
  const required = steps.filter((step) => !step.optional);
  const complete = required.filter((step) => step.complete).length;
  const percentage = Math.round((complete / required.length) * 100);
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="GUIDED ACTIVATION"
        title="Setup & launch"
        description={`${organisation.name} · Complete the controls required for a real purchase, then run the Golden Journey once.`}
      >
        <Button onClick={() => navigate("requisitions")}>Create requisition <ArrowRight className="size-4" /></Button>
      </PageHeader>
      <Panel>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="eyebrow">LAUNCH READINESS</p><p className="mt-2 text-3xl font-semibold tabular-nums">{percentage}%</p><p className="mt-1 text-sm text-[var(--text-secondary)]">{complete} of {required.length} required setup steps complete</p></div>
          <Badge value={percentage === 100 ? "verified" : "in_review"} />
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[var(--surface-secondary)]" aria-label={`${percentage}% setup complete`}><div className="h-full bg-[var(--action)]" style={{ width: `${percentage}%` }} /></div>
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        {steps.map((step, index) => (
          <Panel className="flex flex-col" key={step.title}>
            <div className="flex items-start gap-3">
              {step.complete ? <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[var(--success)]" aria-hidden="true" /> : <CircleDashed className="mt-0.5 size-5 shrink-0 text-[var(--text-muted)]" aria-hidden="true" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><p className="text-xs font-semibold tabular-nums">STEP {index + 1}</p>{step.optional && <Badge value="optional" />}</div>
                <h2 className="mt-2 text-lg font-semibold">{step.title}</h2>
                <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">{step.description}</p>
                <p className="mt-3 text-xs font-medium">{step.evidence}</p>
              </div>
            </div>
            <Button className="mt-5 self-start" variant={step.complete ? "ghost" : "outline"} onClick={() => navigate(step.view)}>{step.action}<ArrowRight className="size-4" /></Button>
          </Panel>
        ))}
      </div>
    </div>
  );
}
